/**
 * E018-S013-T011: FullscreenDatabaseViewExtension
 *
 * Provides:
 * 1. Custom Root-Block that renders ONLY databases (not notes)
 * 2. Database view that uses affine-database (page-mode component)
 *
 * We use di.override (not addImpl) to replace existing views without errors.
 */

import {
  type ViewExtensionContext,
  ViewExtensionProvider,
} from '@blocksuite/affine/ext-loader';
import { BlockViewIdentifier } from '@blocksuite/affine/std';
import type { ExtensionType } from '@blocksuite/affine/store';
import { literal } from 'lit/static-html.js';
import { z } from 'zod';

import { FullscreenDatabaseNoteBlock } from './fullscreen-note-block';
import { FullscreenDatabaseRootBlock } from './fullscreen-root-block';

/**
 * Like BlockViewExtension but uses di.override instead of di.addImpl
 * This allows replacing an existing view without "already exists" errors.
 */
function BlockViewOverride(
  flavour: string,
  view: () => ReturnType<typeof literal>
): ExtensionType {
  return {
    setup: di => {
      // sl-gka: Fix - don't wrap view in another function, it's already a function
      di.override(BlockViewIdentifier(flavour), view);
    },
  };
}

const optionsSchema = z.object({});

export type FullscreenDatabaseViewOptions = z.infer<typeof optionsSchema>;

export class FullscreenDatabaseViewExtension extends ViewExtensionProvider<FullscreenDatabaseViewOptions> {
  override name = 'fullscreen-database-view';

  override schema = optionsSchema;

  override effect() {
    super.effect();
    // Register our custom root block element
    if (!customElements.get('fullscreen-database-root')) {
      customElements.define(
        'fullscreen-database-root',
        FullscreenDatabaseRootBlock
      );
    }
    if (!customElements.get('fullscreen-database-note')) {
      customElements.define(
        'fullscreen-database-note',
        FullscreenDatabaseNoteBlock
      );
    }
  }

  override setup(
    context: ViewExtensionContext,
    _options?: FullscreenDatabaseViewOptions
  ) {
    super.setup(context, _options);

    // Use override (not addImpl) to replace existing views
    context.register([
      // Custom root that renders databases (not notes)
      BlockViewOverride('affine:page', () => {
        return literal`fullscreen-database-root`;
      }),
      // Note uses minimal passthrough (no widgets like KeyboardToolbar)
      BlockViewOverride('affine:note', () => {
        return literal`fullscreen-database-note`;
      }),
      // Database uses page-mode component
      BlockViewOverride('affine:database', () => {
        return literal`affine-database`;
      }),
    ]);
  }
}
